import { useState, useEffect, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * BulkActionsPanel — Panel de acciones masivas que aparece cuando ≥2 usuarios están seleccionados.
 * Permite conceder permisos, revocar permisos o asignar un perfil a múltiples usuarios.
 *
 * @param {{
 *   selectedUserIds: string[],
 *   tenantRoutes: Array<{ id: string, path: string, page_name: string, category: string }>,
 *   profiles: Array<{ id: string, name: string }>,
 *   onDone: () => void
 * }} props
 */
export function BulkActionsPanel({ selectedUserIds, tenantRoutes, profiles, onDone }) {
  const [action, setAction] = useState(null); // "grant" | "revoke" | "assign-profile"
  const [selectedRouteIds, setSelectedRouteIds] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [loading, setLoading] = useState(false);

  const resetDialog = () => {
    setAction(null);
    setSelectedRouteIds([]);
    setSelectedProfileId('');
  };

  const handleRouteToggle = (routeId) => {
    setSelectedRouteIds((prev) => (prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]));
  };

  const handleExecute = async () => {
    setLoading(true);
    try {
      if (action === 'grant') {
        if (selectedRouteIds.length === 0) {
          toast.error('Seleccione al menos una ruta');
          setLoading(false);
          return;
        }
        const { data } = await api.post('/admin/bulk/grant', {
          user_ids: selectedUserIds,
          route_ids: selectedRouteIds,
        });
        const affected = data?.affected ?? selectedUserIds.length;
        const skipped = data?.skipped ?? 0;
        toast.success(`Permisos concedidos: ${affected} afectados, ${skipped} omitidos`);
      } else if (action === 'revoke') {
        if (selectedRouteIds.length === 0) {
          toast.error('Seleccione al menos una ruta');
          setLoading(false);
          return;
        }
        const { data } = await api.post('/admin/bulk/revoke', {
          user_ids: selectedUserIds,
          route_ids: selectedRouteIds,
        });
        const affected = data?.affected ?? selectedUserIds.length;
        const skipped = data?.skipped ?? 0;
        toast.success(`Permisos revocados: ${affected} afectados, ${skipped} omitidos`);
      } else if (action === 'assign-profile') {
        if (!selectedProfileId) {
          toast.error('Seleccione un perfil');
          setLoading(false);
          return;
        }
        const { data } = await api.post('/admin/bulk/assign-profile', {
          user_ids: selectedUserIds,
          profile_id: selectedProfileId,
        });
        const affected = data?.affected ?? selectedUserIds.length;
        const skipped = data?.skipped ?? 0;
        toast.success(`Perfil asignado: ${affected} afectados, ${skipped} omitidos`);
      }
      resetDialog();
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Group routes by category for display
  const routesByCategory = (tenantRoutes || []).reduce((acc, route) => {
    const cat = route.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(route);
    return acc;
  }, {});

  return (
    <>
      {/* Inline actions bar */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-primary/5 border border-primary/20 rounded-lg"
        role="toolbar"
        aria-label="Acciones masivas"
        data-testid="bulk-actions-panel">
        <Badge variant="secondary" className="text-xs" data-testid="bulk-selected-count">
          {selectedUserIds.length} usuarios seleccionados
        </Badge>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setAction('grant')}
            aria-label="Conceder permisos a usuarios seleccionados"
            data-testid="bulk-grant-btn">
            Conceder permisos
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setAction('revoke')}
            aria-label="Revocar permisos de usuarios seleccionados"
            data-testid="bulk-revoke-btn">
            Revocar permisos
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setAction('assign-profile')}
            aria-label="Asignar perfil a usuarios seleccionados"
            data-testid="bulk-assign-profile-btn">
            Asignar perfil
          </Button>
        </div>
      </div>

      {/* Sub-dialog for grant/revoke (route selection) */}
      <Dialog open={action === 'grant' || action === 'revoke'} onOpenChange={(open) => !open && resetDialog()}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{action === 'grant' ? 'Conceder permisos' : 'Revocar permisos'}</DialogTitle>
            <DialogDescription>
              {action === 'grant'
                ? `Seleccione las rutas a conceder a ${selectedUserIds.length} usuarios.`
                : `Seleccione las rutas a revocar de ${selectedUserIds.length} usuarios.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground">{selectedRouteIds.length} ruta(s) seleccionada(s)</p>
            <div className="border rounded-md p-3 space-y-4 max-h-60 overflow-y-auto">
              {Object.entries(routesByCategory).map(([category, routes]) => (
                <div key={category}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{category}</p>
                  <div className="space-y-1.5">
                    {routes
                      .filter((r) => r.classification === 'private')
                      .map((route) => (
                        <label
                          key={route.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                          <Checkbox
                            checked={selectedRouteIds.includes(route.id)}
                            onCheckedChange={() => handleRouteToggle(route.id)}
                            aria-label={`Seleccionar ruta ${route.page_name}`}
                            data-testid={`bulk-route-${route.id}`}
                          />
                          <span className="text-sm">{route.page_name}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{route.path}</span>
                        </label>
                      ))}
                  </div>
                </div>
              ))}
              {Object.keys(routesByCategory).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No hay rutas disponibles</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleExecute}
              disabled={loading || selectedRouteIds.length === 0}
              data-testid="bulk-execute-btn">
              {loading ? 'Procesando...' : action === 'grant' ? 'Conceder' : 'Revocar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-dialog for assign-profile (profile selection) */}
      <Dialog open={action === 'assign-profile'} onOpenChange={(open) => !open && resetDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar perfil</DialogTitle>
            <DialogDescription>Seleccione el perfil a asignar a {selectedUserIds.length} usuarios.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger className="w-full" aria-label="Seleccionar perfil">
                <SelectValue placeholder="Seleccionar perfil..." />
              </SelectTrigger>
              <SelectContent>
                {(profiles || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {profiles?.length === 0 && <p className="text-sm text-muted-foreground">No hay perfiles disponibles</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleExecute}
              disabled={loading || !selectedProfileId}
              data-testid="bulk-assign-execute-btn">
              {loading ? 'Procesando...' : 'Asignar perfil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
